/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  CSSProperties,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react"
declare type Placement = import("react-overlays/usePopper").Placement

/**
 * テキスト入力用のラッパー
 * @param {ReactPropTypes} { name:項目の論理名(string), value:設定対象のstate(string), setValue:設定対象のstateに対するsetState(function), id:controlのID(string), required:必須チェックを行うか(boolean) }
 * @return {ReactComponentElement}
 */
const TextInput: React.FC<{
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>
  onChange?: React.ChangeEventHandler<HTMLInputElement>
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  className?: string
  style?: CSSProperties
  autoFocus?: boolean
  name: string
  value: string | undefined
  setValue: (value: string) => void
  id?: string
  isInlineBlockComponent?: boolean
  isFulFilledWidth?: boolean
  size?: number
  maxLength?: number
  isReadOnly?: boolean
  isRequired?: boolean
  placeholder?: string
  tooltipPlacement?: Placement
  type?: "text" | "email" | "tel" | "number" | "password"
  pattern?: string
  setInvalidMessage?: (value: string) => void
  autoComplete?: "off" | "nope"
  tabIndex?: number
  ref?: RefObject<HTMLInputElement>
}> = ({
  onKeyDown,
  onChange,
  onFocus,
  onBlur,
  style,
  className,
  autoFocus,
  name,
  value,
  setValue,
  id,
  isInlineBlockComponent,
  isFulFilledWidth,
  size,
  maxLength,
  isReadOnly: readOnly,
  isRequired: required,
  placeholder,
  type = "text",
  pattern,
  setInvalidMessage,
  autoComplete,
  tabIndex,
  ref,
}) => {
  const parentRef = useRef(null)
  const [isError, setIsError] = useState<boolean>(false)

  const setErrorMessage = (message: string) => {
    //エラーメッセージを返す
    if (setInvalidMessage ?? false) {
      setInvalidMessage!(message)
    }
  }

  function inValid(e: React.FocusEvent<HTMLInputElement>) {
    let message = ""
    let isErrorInner = false
    if (e.target.validity.valueMissing) {
      isErrorInner = true
      message = `${name}は必須入力です。`
    } else if (e.target.validity.patternMismatch) {
      isErrorInner = true
      message = `${name}の入力形式を確認してください。`
    } else if (e.target.validity.tooLong) {
      isErrorInner = true
      message = `${name}の値が大きすぎます。`
    } else if (e.target.validity.tooShort) {
      isErrorInner = true
      message = `${name}の値が小さすぎます。`
    } else if (type === "email" && e.target.validity.typeMismatch) {
      isErrorInner = true
      message = `${name}をxxx@yyyyの形式に合わせて入力してください`
    }

    setErrorMessage(message)
    setIsError(isErrorInner)
  }

  const innerRef = React.createRef<HTMLInputElement>()

  useEffect(() => {
    if (autoFocus) {
      if (ref) {
        ref.current?.focus()
      } else {
        innerRef.current?.focus()
      }
    }
  }, [])

  // placeHolderとfloatingLabel用
  const [isShowFloatingLabel, setIsShowFloatingLabel] = useState<boolean>()

  useEffect(() => {
    if (placeholder && value) {
      setIsShowFloatingLabel(true)
    } else {
      setIsShowFloatingLabel(false)
    }
  }, [placeholder, value])

  return (
    <>
      <div
        className={[
          "floating-frame",
          isInlineBlockComponent ? "d-inline-block" : "",
          isFulFilledWidth ? "w-100" : "",
        ].join(" ")}>
        {isShowFloatingLabel && (
          <span className="floating-label">{placeholder}</span>
        )}
        <div
          className={[
            "wj-control",
            "wj-content",
            "wj-dropdown",
            required ? "required_input" : "",
            isFulFilledWidth ? "w-100" : "",
          ].join(" ")}
          ref={parentRef}>
          <div className="wj-template">
            <div className="wj-input">
              <div className="wj-input-group">
                <input
                  onKeyDown={onKeyDown}
                  onBlur={onBlur}
                  style={style}
                  autoFocus={autoFocus}
                  onFocus={onFocus}
                  pattern={
                    pattern
                      ? pattern
                      : type === "tel"
                      ? "[0-9]{2,5}-?[0-9]{1,4}-?[0-9]{4}"
                      : undefined
                  }
                  ref={ref ?? innerRef}
                  id={id}
                  className={[
                    "wj-form-control",
                    "wj-form-content",
                    "text-input",
                    isError ? "is-error" : "",
                    readOnly ? "read-only" : "",
                    className != null ? className : "",
                  ].join(" ")}
                  type={type}
                  value={value ?? ""}
                  readOnly={readOnly}
                  required={required}
                  size={size}
                  tabIndex={tabIndex}
                  maxLength={
                    maxLength ? maxLength : type === "tel" ? 13 : undefined
                  }
                  placeholder={isShowFloatingLabel ? "" : placeholder ?? ""}
                  autoComplete={autoComplete ?? "off"}
                  onChange={(e) => {
                    onChange && onChange(e)
                    setValue && setValue(e.target.value)
                  }}
                  onInvalid={(e: React.FocusEvent<HTMLInputElement>) =>
                    inValid(e)
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default TextInput
